/** Registration declarations ("bản khai"): templates, a subject's declarations, reviews. */
export const declarations = {
  status: {
    GENERATED: 'Generated',
    UPLOADED: 'Uploaded, being checked',
    VERIFIED: 'Accepted (digital signature)',
    REVIEW_REQUIRED: 'Awaiting staff review',
    APPROVED: 'Approved by staff',
    REJECTED: 'To be supplemented',
  },
  uploadStatus: {
    PENDING: 'Being checked',
    VERIFIED: 'Accepted automatically',
    REVIEW_REQUIRED: 'Awaiting review',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    SUPERSEDED: 'Replaced by a newer upload',
  },
  uploadKind: { SIGNED_PDF: 'Digitally signed PDF', SCAN: 'Scan / hand-signed' },
  checkStatus: { PASS: 'Pass', FAIL: 'Fail', UNKNOWN: 'Unknown' },
  check: {
    DECLARED_DATA: 'Declared data unchanged',
    TEMPLATE_VERSION: 'Template version available',
    PDF_STRUCTURE: 'PDF without active content',
    SIGNATURE_PRESENT: 'Digital signature present',
    SIGNATURE_FORMAT: 'Signature format (CMS / CAdES)',
    SIGNATURE_COVERS_FILE: 'Signature covers the whole file',
    SAME_AS_GENERATED: 'Same content as the generated declaration',
    CMS_SIGNATURE_VALID: 'Signature cryptographically valid',
    DIGEST_MATCHES: 'Not changed after signing',
    KEY_USAGE: 'Signing certificate',
    SIGNING_TIME: 'Signing time',
    SIGNATURE_TIMESTAMP: 'Time-stamp (RFC 3161)',
    FORM_PURPOSE: 'Form type (return / parental consent: decided by staff)',
    VERIFICATION_PATH: 'The verification path of the subject type (individual: eKYC; organisation: subject declaration)',
    signature: 'Signature {{n}}',
    documentTimestamp: 'Document time-stamp {{n}}',
    CHAIN_TRUSTED: 'Chain to a trusted Vietnamese CA',
    NOT_REVOKED: 'Certificate not revoked (OCSP / CRL)',
    SIGNER_IDENTITY: 'Signer is the registrant (CCCD / MST)',
    SIGNER_NAME: 'Name in the certificate',
    SCAN_IMAGE: 'Readable page image',
    QR_CODE: "QR code of this declaration",
    OCR: 'Text read locally (OCR)',
    field: 'Field: {{label}}',
  },
  // `FIELD:<key>` when the check does not carry its template label (the dossier queue's failed checks).
  fieldName: {
    address: 'Contact address',
    birth_date: 'Date of birth',
    business_address: 'Business address',
    business_name: 'Household business name',
    domains: 'Domain names',
    head_office: 'Head office address',
    household_code: 'Household business registration number',
    id_number: 'Personal ID / passport number',
    legal_name: 'Full name',
    minor_name: 'Name of the ward',
    org_name: 'Organisation name',
    owner_name: 'Name of the household business owner',
    permanent_address: 'Permanent address',
    rep_id_number: "Representative's ID number",
    rep_name: "Representative's name",
    tax_code: 'Tax code',
  },
  checks: {
    title: 'Checks',
    check: 'Check',
    result: 'Result',
    reason: 'Reason',
    empty: 'Not checked yet.',
    allPass: 'Every check passed.',
    attention: '{{count}} check(s) need attention.',
    noTimestamp: 'Signing time as claimed by the signer (no time-stamp).',
  },
  sig: {
    signer: 'Signer',
    ca: 'Issuing CA',
    signingTime: 'Signing time (claimed)',
    timestamp: 'Time-stamp',
    timestampValid: 'yes, valid',
    timestampInvalid: 'yes, not valid',
    noTimestamp: 'none',
    covers: 'Coverage',
    wholeDocument: 'the whole document',
    partOfDocument: 'part of the document (content added after signing)',
    byteRange: 'Byte range',
    valid: 'Valid',
    yes: 'yes',
    no: 'no',
  },
  trust: {
    title: 'Trusted root certificates',
    description: 'The CA certificates admin-api loaded to check digital signatures on declarations (and time-stamps). Read-only; changed by file and a restart.',
    loadError: 'Could not load the trust store',
    count: 'Root certificates loaded',
    allowList: 'Fingerprints in the allow-list',
    noExtraDir: 'No server directory configured',
    emptyTitle: 'The trust store is empty',
    emptyBody: 'No root certificate yet: every digitally signed declaration goes to staff review.',
    howTo: 'Sources: {{dir}} (repository) and {{extra}} (server, filled by subsystems/identity/modules/declarations/tools/fetch-vn-ca.sh). A server certificate is used only when its SHA-256 fingerprint is in trusted-fingerprints.txt — check it through a second channel (NEAC) first, then restart api, admin-api and identity-worker.',
    anchors: 'Certificates in use',
    subject: 'Certificate',
    issuer: 'Issued by',
    selfSigned: 'self-signed (root)',
    validity: 'Validity',
    expired: 'expired',
    fingerprint: 'SHA-256 fingerprint',
    file: 'File',
    sourceRepo: 'repository',
    sourceExtra: 'server (allow-listed)',
    ignored: 'Certificates found but not used',
    ignoredDescription: 'Not in the allow-list, or not a CA certificate.',
    reason: 'Reason',
    ignoredReason: { NOT_IN_ALLOWLIST: 'not in trusted-fingerprints.txt', NOT_A_CA: 'not a CA certificate' },
  },
  documentTypes: {
    title: 'Document types',
    description: 'The documents a domain dossier may hold: who uploads each one, its file formats, the TLDs it applies to and its VNNIC dossier type. The upload on the domain page, the required documents and the files sent to VNNIC follow this list.',
    col: { name: 'Name', type: 'Type', extensions: 'Formats', tlds: 'TLDs', actions: 'Actions' },
    badge: { download: 'Template', required: 'Required', requiredFor: 'Required: {{ops}}', individual: 'Individuals', organization: 'Organisations', ekyc: 'eKYC', onlyDownload: 'Customer downloads only', onlyAdmin: 'Staff only', autoSign: 'Signed by the registrar automatically' },
    flag: { download: 'Template available for download', ekyc: 'Filled by eKYC', onlyDownload: 'Customer only downloads the template (staff upload)', onlyAdmin: 'Staff only', active: 'In use' },
    field: {
      nameVi: 'Name (Vietnamese)',
      nameEn: 'Name (English)',
      flags: 'Type',
      required: 'Required',
      requiredToggle: 'The dossier must hold this type before the registry command is sent',
      requiredOps: 'Required for operations',
      requiredHint: 'Applies exactly within the scope below (TLDs and registrants), on every TLD — .vn and international alike. A type filled by eKYC counts once the registrant passed eKYC; the domain declaration once the dossier holds one (generated, the subject declaration or the customer-signed one).',
      subjects: 'Applies to registrants',
      hstmFile: 'File (VNNIC file type)',
      hstmDocument: 'Dossier type (VNNIC)',
      methods: 'Verification methods it applies to',
      methodsHint: 'None selected: every method. Used for the files sent to VNNIC (HSTM) only; whether the type is required follows "Required".',
      extensions: 'Allowed file formats',
      extensionsHint: 'Type and press Enter: pdf, png, jpg, jpeg.',
      maxSize: 'Maximum size (MB)',
      template: 'Template',
      tlds: 'Applies to TLDs',
      zoneFilter: 'Filter TLDs…',
    },
    subject: { ALL: 'All', INDIVIDUAL: 'Individuals', ORGANIZATION: 'Organisations (every non-individual registrant)' },
    zonesPicked: '{{n}} TLDs selected',
    zonesLoading: 'Loading TLDs…',
    operation: { REGISTER: 'New registration', CHANGE_INFORMATION: 'Change of information', CHANGE_REGISTRANT_NAME: 'Registrant name change', RETURN: 'Domain return', TRANSFER_RIGHT_OF_USE: 'Transfer of the right of use', CHANGE_REGISTRAR: 'Registrar change' },
    none: '— None —',
    noTemplate: 'This document type has no template',
    editTemplate: 'Edit template',
    allTlds: 'Every TLD',
    allTldsShort: 'Every TLD',
    someTlds: 'Choose TLDs',
    hstmShort: 'VNNIC file #{{n}}',
    signAuto: 'Signed by the registrar automatically in every dossier',
    signOptional: 'Signed by the registrar when staff send it',
    updated: 'Updated {{at}} by {{by}}',
    edit: 'Edit',
    view: 'View',
    editHint: 'Changes are logged with their reason.',
    save: 'Save',
    readOnly: 'Only compliance officers can change document types.',
    empty: 'No document types',
  },
  checkSettings: {
    title: 'Declaration checks',
    description: 'What happens when an uploaded declaration fails a check: staff review (default) or an automatic refusal with the reason shown to the customer. Each failure kind is set on its own; every change is audited with a reason.',
    rootCa: 'A root CA missing from the trust store is never a failure: a signed declaration is accepted automatically when its signature is intact and covers the whole document, the MST / CCCD in the certificate is the subject\'s and the certificate was valid at the signing time; the chain is recorded for information.',
    section: 'When a declaration fails',
    modes: { STAFF_REVIEW: 'Staff review', AUTO_REJECT: 'Refuse automatically, tell the customer why' },
    kinds: {
      integrity_failure: { label: 'Signature not intact', hint: 'The digital signature does not verify, or the file changed (not the generated declaration).' },
      coverage_failure: { label: 'Signature does not cover the whole document', hint: 'Content was added after the last signature.' },
      identifier_mismatch: { label: 'Signer is not the subject', hint: 'The MST / CCCD or name in the certificate does not match the subject.' },
      paper_rejected: { label: 'Paper copy is another document', hint: 'Read on the server (OCR): another declaration, or nothing of the subject on it.' },
    },
    save: 'Save',
    readOnly: 'Only staff allowed to “Override a critical compliance decision” can change this.',
    updated: 'Updated {{at}} by {{by}}',
    someone: 'a staff member',
  },
  vnZones: {
    title: '.vn zone rules',
    description: 'Who may register each .vn zone, extra documents, manual activation, VNNIC promotion codes — defined in config/vn-zones.yaml (read-only).',
    loadError: 'Could not load the .vn zone rules',
    fileNote: 'Version {{version}}, file {{source}}: edit it, then restart admin-api, api and domain-worker.',
    unconfirmedCount: '{{count}} rule(s) not yet confirmed by the owner against the source.',
    ruleTitle: 'Registration rules (.vn)',
    fileDefined: 'From config/vn-zones.yaml.',
    defaultsApply: 'No entry for this zone: the defaults apply.',
    zone: 'Zone',
    defaults: '(defaults)',
    registrantTypes: 'Registrant types allowed',
    nationalities: 'Nationality',
    any: 'any',
    manualActivation: 'Activation',
    manual: 'manual (staff approve)',
    automatic: 'automatic',
    promoCode: 'VNNIC promotion code',
    promoNone: 'not applicable',
    promo: { eligible: 'accepted', required: 'required' },
    documents: 'Extra documents',
    formOnly: 'only the declaration of the registrant type',
    source: 'Source',
    status: 'Confirmed',
    confirmed: 'confirmed',
    unconfirmed: 'not confirmed',
    type: { INDIVIDUAL: 'individuals', ORGANISATION: 'organisations, enterprises', HOUSEHOLD_BUSINESS: 'household businesses', STATE_AGENCY: 'state agencies' },
  },
  templates: {
    title: 'Declaration forms',
    description: 'The "bản khai đăng ký tên miền" forms the platform generates, with the fields bound to the registrant\'s data.',
    fileDefined: 'Defined in files ({{source}}): edit the YAML file, then restart admin-api, api and identity-worker. Nothing is edited here.',
    loadError: 'Could not load the declaration forms',
    form: 'Form',
    version: 'Version',
    file: 'File',
    subjectTypes: 'Registrants',
    legalBasis: 'Legal basis',
    source: 'Layout source',
    layoutChecked: 'Layout checked against the official form',
    layoutNotChecked: 'Layout to be checked against the official form',
    preview: 'Preview PDF (sample data)',
    fields: 'Fields',
    label: 'Label',
    key: 'Key',
    binding: 'Filled from',
    required: 'Required',
    compare: 'Compared on scans',
    sensitive: 'Masked',
    fixedText: 'Fixed text',
    blank: 'Left blank (filled by hand)',
    signature: 'Signature block',
    trustTitle: 'Trusted certification authorities',
    trustDescription: 'Digital signatures must chain to one of these certificates ({{dir}}). Files in the repository; see its README to add a CA.',
    trustEmpty: 'The trust store is empty: every digitally signed declaration goes to staff review.',
    trustFile: 'File',
    trustSubject: 'Certificate',
    trustFingerprint: 'SHA-256 fingerprint',
  },
  subject: {
    purpose: 'Form',
    purposes: {
      SUBJECT_DECLARATION: 'Subject declaration (an organisation, signed once)',
      REGISTRATION: 'Registration (form 01a / 01b / 01c)',
      PARENTAL_CONSENT: "Parent's / guardian's consent (form 02)",
      RETURN: 'Domain return (form 03a / 03b / 03c)',
    },
    purposeHint: {
      SUBJECT_DECLARATION: 'An organisation digitally signs its registration information once; every later domain gets its declaration generated. When it cannot sign digitally: print, sign, stamp and upload the scan — read on the server, decided by staff. Sign again when the data changes.',
      REGISTRATION: 'The form of the subject type: organisation 01a, household business 01b, individual 01c. Generated for each domain; an individual is verified by eKYC and uploads nothing.',
      PARENTAL_CONSENT: 'Registrant under 18: the parent / guardian fills in their part by hand and signs. Always decided by staff.',
      RETURN: 'One domain of this subject and the reason. Always decided by staff.',
    },
    returnReason: 'Reason for the return',
    tab: 'Declarations',
    title: 'Declarations (bản khai)',
    description: 'Generated from the current version of this subject. An individual is verified by eKYC: each domain declaration is generated, nothing to sign. An organisation signs its subject declaration once (digitally, or on paper, signed and stamped); each domain declaration is generated. The registrar signs only the domain declaration, after checking the dossier.',
    generate: 'Generate declaration',
    generateTitle: 'Generate a declaration',
    domains: 'Domain names',
    domainsHint: 'One per line (up to 50). The declaration lists them all.',
    generated: 'Declaration generated.',
    empty: 'No declaration yet',
    emptyBody: 'An organisation: generate its subject declaration, sign it digitally (or print, sign and stamp it), then upload the result.',
    created: 'Created',
    template: 'Form',
    status: 'Status',
    domainsCol: 'Domains',
    uploads: 'Uploads',
    noUploads: 'Nothing uploaded yet.',
    download: 'Generated PDF',
    upload: 'Upload signed file',
    uploadTitle: 'Upload the signed declaration',
    uploadHint: 'A digitally signed PDF, or a scan / photo of the printed form, signed and stamped (PDF, PNG or JPEG, up to 20 MB). Paper is read on the server (OCR) and compared with the data; staff always decide. Received by post or e-mail: say so in the reason.',
    file: 'File',
    uploaded: 'Uploaded: the platform checks the file now.',
    viewFile: 'View file',
    workItem: 'Open review',
    signer: 'Signer',
    signedAs: { SUBJECT: 'the registrant', REPRESENTATIVE: 'the legal representative' },
    revocation: 'Revocation',
    chain: 'Certificate chain',
    signingTime: 'Signed at',
    kind: 'Kind',
  },
  read: {
    title: 'Open a declaration file',
    description: 'Declarations carry identity data: the read is audited with your reason and needs the identity-document permission.',
    reason: 'Reason',
    reasonHint: 'Why you need to see the file (e.g. the work item you are reviewing).',
    open: 'Open',
  },
  review: {
    title: 'Declaration under review',
    matches: 'Every automated check passed. A hand signature cannot be verified by the platform: compare the scan with the generated declaration, then approve.',
    notEverything: 'Some checks failed or could not be decided. Look at the table and the files before deciding.',
    signedNote: 'A digitally signed declaration that did not pass every check.',
    scanNote: 'A paper declaration (signed and stamped): read on the server, always decided by staff.',
    approveEffect: 'Approving an organisation\'s paper declaration verifies the subject (method 5 — manual: the registrar signs the domain declaration). An individual is verified by eKYC only: an individual\'s declaration is never approved. A form 02 / 03 may be accepted as a document (no verification of the registrant); a digitally signed form 03 becomes the domain\'s VNNIC "return" dossier. Rejecting asks the registrant to supplement it (e-mail).',
    generated: 'Generated declaration',
    uploaded: 'Uploaded file',
    show: 'Show side by side',
    openSubject: "Registrant's declarations",
    paper: {
      title: 'Paper declaration report (OCR on the server)',
      status: {
        MATCH: 'Matches the data: staff approve',
        MISMATCH: 'Some fields differ: staff read the warning and decide',
        REJECTED: 'Not this declaration: refused automatically, the customer was told',
        UNREADABLE: 'Could not be read, review by hand',
      },
      fields: 'Fields matching: {{matched}} / {{compared}}',
      signature: 'Signature',
      stamp: 'Red stamp',
      detected: 'found ({{confidence}})',
      notDetected: 'not found',
      ocr: '{{lines}} lines read, mean confidence {{confidence}}',
      engine: 'Reader',
      staffDecides: 'The automated reading is advice only: staff always decide.',
    },
  },
  verify: {
    title: 'How the registrant is verified',
    body: 'A domain activates once the registrant is verified, the same for every domain: an individual by eKYC, an organisation by a valid digitally signed subject declaration (or a paper one, signed and stamped, approved by staff). Anything that does not match automatically goes to the work queue.',
    ekyc: 'Individual: eKYC (VNNIC eKYC API) — the only way',
    declaration: 'Organisation: subject declaration digitally signed once (or a paper one, signed and stamped, approved by staff)',
    open: 'Registrant page: declarations',
  },
};
